import React, { useEffect, useState, useCallback, useRef } from "react";
import { Loader2, LayoutDashboard, Users, ChevronDown, ChevronRight, BarChart2, Filter } from "lucide-react";
import { toast } from "sonner";
import { sindicatoService } from "../../services/sindicato.service";
import { userService } from "../../services/user.service";
import { processService } from "../../services/process.service";
import { DashboardTab } from "../management/DashboardTab";
import { useAuthStore } from "../../store/useAuthStore";
import { useThemeStore } from "../../store/useThemeStore";

function ProcessoMultiSelect({ processos, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const label =
    selectedIds.size === 0
      ? "Todos os processos"
      : `${selectedIds.size} processo(s) selecionado(s)`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="border rounded px-3 py-2 text-sm flex items-center gap-2 bg-card text-foreground hover:bg-muted transition-colors"
      >
        <Filter size={14} />
        <span>{label}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-10 min-w-64 p-2">
          {processos.length === 0 ? (
            <p className="text-xs text-muted-foreground px-2 py-2">Nenhum processo disponível.</p>
          ) : (
            processos.map((p) => (
              <label
                key={p.id}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted rounded cursor-pointer text-sm text-foreground"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(p.id)}
                  onChange={() => toggle(p.id)}
                  className="accent-slate-700"
                />
                {p.nome_processo}
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function CedentesTab({ processos }) {
  const [cedentes, setCedentes] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (q = "", ids = new Set()) => {
    setLoading(true);
    try {
      const data = await userService.getCedentes(q, null, null, Array.from(ids));
      setCedentes(data);
    } catch {
      toast.error("Erro ao carregar substituídos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load("", selectedIds); }, [load, selectedIds]);

  const handleSearch = (e) => {
    const v = e.target.value;
    setSearch(v);
    if (v.length === 0 || v.length >= 3) load(v, selectedIds);
  };

  const handleProcessoChange = (next) => {
    setSelectedIds(next);
    setSearch("");
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <ProcessoMultiSelect
          processos={processos}
          selectedIds={selectedIds}
          onChange={handleProcessoChange}
        />
        <input
          className="border rounded px-3 py-2 text-sm w-full max-w-sm"
          placeholder="Buscar por nome ou CPF..."
          value={search}
          onChange={handleSearch}
        />
      </div>
      {loading ? (
        <div className="flex justify-center items-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando...
        </div>
      ) : cedentes.length === 0 ? (
        <p className="text-center text-muted-foreground py-16 text-sm">Nenhum substituído encontrado.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted border-b border-border">
                <th className="py-3 px-4 text-left font-semibold text-muted-foreground">Nome</th>
                <th className="py-3 px-4 text-left font-semibold text-muted-foreground">CPF</th>
                <th className="py-3 px-4 text-left font-semibold text-muted-foreground">E-mail</th>
                <th className="py-3 px-4 text-left font-semibold text-muted-foreground">Telefone</th>
              </tr>
            </thead>
            <tbody>
              {cedentes.map((c) => (
                <tr key={c.id} className="bg-card border-b border-border hover:bg-muted">
                  <td className="py-3 px-4 font-medium text-foreground">{c.nome}</td>
                  <td className="py-3 px-4 text-muted-foreground">{c.cpf}</td>
                  <td className="py-3 px-4 text-muted-foreground">{c.email || "—"}</td>
                  <td className="py-3 px-4 text-muted-foreground">{c.telefone || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function SindicatoView() {
  const { hasPermission } = useAuthStore();
  const canSeeCedentes = hasPermission("cedentes");
  const { theme } = useThemeStore();
  const logoSrc = theme.logo_url || "/logo.png";
  const sindicatoNome = theme.header_title || null;

  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeViewId, setActiveViewId] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashStats, setDashStats] = useState({});
  const [loadingStats, setLoadingStats] = useState(false);
  const [byProcessStats, setByProcessStats] = useState({});
  const [loadingByProcess, setLoadingByProcess] = useState(false);
  const [expandedProcessos, setExpandedProcessos] = useState({});
  const [processos, setProcessos] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const promises = [sindicatoService.getViews()];
        if (canSeeCedentes) promises.push(processService.getProcessos());
        const [viewsData, processosData] = await Promise.all(promises);
        setViews(viewsData);
        if (viewsData.length > 0) setActiveViewId(viewsData[0].id);
        if (processosData) setProcessos(processosData);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Erro ao carregar dados.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [canSeeCedentes]);

  useEffect(() => {
    if (!activeViewId) return;

    if (activeTab === "dashboard" && !dashStats[activeViewId]) {
      setLoadingStats(true);
      sindicatoService
        .getViewDashboard(activeViewId)
        .then((d) => setDashStats((prev) => ({ ...prev, [activeViewId]: d })))
        .catch(() => toast.error("Erro ao carregar dashboard."))
        .finally(() => setLoadingStats(false));
    }

    if (activeTab === "por-processo" && !byProcessStats[activeViewId]) {
      setLoadingByProcess(true);
      sindicatoService
        .getViewDashboardByProcess(activeViewId)
        .then((d) => setByProcessStats((prev) => ({ ...prev, [activeViewId]: d })))
        .catch(() => toast.error("Erro ao carregar dados por processo."))
        .finally(() => setLoadingByProcess(false));
    }
  // dashStats/byProcessStats intencionalmente fora: usados só como guard de cache
  }, [activeViewId, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleProcesso = (processoId) => {
    setExpandedProcessos((prev) => ({ ...prev, [processoId]: !prev[processoId] }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando...
      </div>
    );
  }

  if (views.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
        <LayoutDashboard className="w-10 h-10 opacity-30" />
        <p className="text-sm">Nenhum painel disponível no momento.</p>
      </div>
    );
  }

  const activeStats = dashStats[activeViewId] ?? null;

  return (
    <div className="w-full max-w-5xl mx-auto px-4 md:px-8 py-8">
      {/* Cabeçalho com logo do sindicato */}
      <div className="flex items-center gap-4 mb-8">
        <img src={logoSrc} alt="Logo" className="h-14 object-contain" />
        {sindicatoNome && (
          <h1 className="text-xl font-bold text-foreground">{sindicatoNome}</h1>
        )}
      </div>

      {/* Sindicato tabs (when multiple views) */}
      {views.length > 1 && (
        <div className="border-b border-border flex gap-1 mb-6">
          {views.map((view) => (
            <button
              key={view.id}
              onClick={() => setActiveViewId(view.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeViewId === view.id
                  ? "border-slate-800 text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              {view.nome}
            </button>
          ))}
        </div>
      )}

      {/* Section tabs */}
      <div className="flex gap-1 mb-6 border-b border-border">
        <button
          onClick={() => setActiveTab("dashboard")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "dashboard"
              ? "border-slate-800 text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <LayoutDashboard className="w-4 h-4" /> Dashboard
        </button>
        <button
          onClick={() => setActiveTab("por-processo")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
            activeTab === "por-processo"
              ? "border-slate-800 text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          <BarChart2 className="w-4 h-4" /> Por Processo
        </button>
        {canSeeCedentes && (
          <button
            onClick={() => setActiveTab("cedentes")}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === "cedentes"
                ? "border-slate-800 text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <Users className="w-4 h-4" /> Substituídos
          </button>
        )}
      </div>

      {/* Content */}
      {activeTab === "dashboard" && (
        loadingStats && !activeStats ? (
          <div className="flex justify-center items-center py-24 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando dashboard...
          </div>
        ) : activeStats ? (
          <DashboardTab stats={activeStats} />
        ) : null
      )}

      {activeTab === "por-processo" && (
        loadingByProcess && !byProcessStats[activeViewId] ? (
          <div className="flex justify-center items-center py-24 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Carregando dados por processo...
          </div>
        ) : byProcessStats[activeViewId]?.length === 0 ? (
          <p className="text-center text-muted-foreground py-16 text-sm">Nenhum processo associado a este painel.</p>
        ) : (
          <div className="space-y-3">
            {(byProcessStats[activeViewId] ?? []).map((item) => {
              const isExpanded = !!expandedProcessos[item.processo_id];
              const totalResponderam = item.total_acordo + item.total_cessao;
              return (
                <div key={item.processo_id} className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
                  <button
                    onClick={() => toggleProcesso(item.processo_id)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                      <span className="font-medium text-foreground text-sm">{item.nome_processo}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span><span className="font-semibold text-foreground">{item.total}</span> substituídos</span>
                      <span><span className="font-semibold text-indigo-600">{totalResponderam}</span> aceitaram</span>
                      {!item.tem_valores && (
                        <span className="bg-muted text-muted-foreground rounded px-2 py-0.5 text-xs">Sem valores</span>
                      )}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="border-t border-border px-5 pt-2 pb-4">
                      <DashboardTab stats={item} temValores={item.tem_valores} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {activeTab === "cedentes" && canSeeCedentes && <CedentesTab processos={processos} />}
    </div>
  );
}
